// Faz 12a (2026-04-29) — Audit Log Viewer
// Tum admin islemlerinin (login_lockout, ORDER_APPROVED, IMPORT_PRODUCTS, vb.)
// merkezi gorunumu. Filter (action, actor, tarih, serbest arama) + pagination
// + detail JSON modal. Compliance + operasyonel debug icin.
import React, { useEffect, useState } from "react";
import {
  Box, Typography, Card, Table, TableHead, TableRow, TableCell, TableBody,
  TextField, MenuItem, Button, Chip, IconButton, Tooltip, CircularProgress,
  Pagination, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Grid
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { getAuditLog, getAuditLogActions } from "../services/api";

// Hata isimlerinde olan action'lar kirmizi rozetli gosterilir
const isError = (action) =>
  /FAILED|lockout|REJECT/i.test(action || "");

const isSuccess = (action) =>
  /APPROVED|SUCCESS|START|RESUME/i.test(action || "");

const fmtDateTime = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  return d.toLocaleString("tr-TR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit"
  });
};

// Action rozeti — error/success/info renk kodu
const ActionChip = ({ action }) => {
  if (!action) return <Chip size="small" label="?" />;
  if (isError(action)) return <Chip size="small" color="error" label={action} sx={{ maxWidth: 360 }} />;
  if (isSuccess(action)) return <Chip size="small" color="success" label={action} sx={{ maxWidth: 360 }} />;
  return <Chip size="small" variant="outlined" label={action} sx={{ maxWidth: 360 }} />;
};

const PAGE_SIZE = 50;

export default function AuditLog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [actions, setActions] = useState([]); // distinct action listesi (filter dropdown)
  const [filter, setFilter] = useState({ action: "", q: "", from: "", to: "" });
  const [detail, setDetail] = useState(null); // detay modal icerigi (audit kayit)

  const load = (p = page) => {
    setLoading(true);
    setErr(null);
    const offset = (p - 1) * PAGE_SIZE;
    getAuditLog({
      ...filter,
      limit: PAGE_SIZE,
      offset
    })
      .then((r) => {
        setItems(r.data.items || []);
        setTotal(r.data.total || 0);
      })
      .catch((e) => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };

  // Distinct action listesi — sadece bir kez yuklenir (filter dropdown icin)
  useEffect(() => {
    getAuditLogActions().then((r) => setActions(r.data.actions || [])).catch(() => {});
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(page); }, [page]);

  const applyFilter = () => { setPage(1); load(1); };
  const clearFilter = () => {
    setFilter({ action: "", q: "", from: "", to: "" });
    setPage(1);
    setTimeout(() => load(1), 0);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h5" fontWeight={800}>📋 Audit Log</Typography>
          <Typography variant="body2" color="text.secondary">
            Tüm admin işlemleri ve sistem olayları — {total.toLocaleString("tr-TR")} kayıt
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title="Yenile">
            <IconButton onClick={() => load(page)}><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filter çubuğu */}
      <Card sx={{ p: 2, borderRadius: 3, mb: 2 }}>
        <Grid container spacing={2} alignItems="end">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select fullWidth label="İşlem (action)" size="small"
              value={filter.action}
              onChange={(e) => setFilter({ ...filter, action: e.target.value })}
            >
              <MenuItem value="">Hepsi</MenuItem>
              {actions.map((a) => (
                <MenuItem key={a.action} value={a.action}>{a.action} ({a.c})</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth label="Serbest arama (action / id / details)" size="small"
              value={filter.q}
              onChange={(e) => setFilter({ ...filter, q: e.target.value })}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              type="date" fullWidth label="Başlangıç" size="small" InputLabelProps={{ shrink: true }}
              value={filter.from}
              onChange={(e) => setFilter({ ...filter, from: e.target.value })}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              type="date" fullWidth label="Bitiş" size="small" InputLabelProps={{ shrink: true }}
              value={filter.to}
              onChange={(e) => setFilter({ ...filter, to: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Box display="flex" gap={1}>
              <Button variant="contained" fullWidth onClick={applyFilter} sx={{ bgcolor: "#1565C0" }}>Filtrele</Button>
              <Tooltip title="Filtreyi temizle">
                <IconButton onClick={clearFilter} size="small"><FilterAltOffIcon /></IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Card>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={5}><CircularProgress /></Box>
        ) : items.length === 0 ? (
          <Box p={6} textAlign="center" color="text.secondary">
            <Typography>Filtreye uygun kayıt yok.</Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: "#f8f9fa" }}>
                <TableRow>
                  <TableCell width={170}>Zaman</TableCell>
                  <TableCell>İşlem</TableCell>
                  <TableCell>Yapan (Actor)</TableCell>
                  <TableCell>Hedef Kullanıcı</TableCell>
                  <TableCell>Kaynak</TableCell>
                  <TableCell width={120}>IP</TableCell>
                  <TableCell width={60}>Detay</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((it) => (
                  <TableRow key={it.id} hover>
                    <TableCell sx={{ whiteSpace: "nowrap", fontSize: 12 }}>
                      {fmtDateTime(it.created_at)}
                    </TableCell>
                    <TableCell>
                      <ActionChip action={it.action} />
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {it.actor_name || it.actor_email || (it.actor_id ? `#${it.actor_id}` : "—")}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {it.target_name || it.target_email || (it.user_id ? `#${it.user_id}` : "—")}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: "#666" }}>
                      {it.resource_type ? `${it.resource_type}${it.resource_id ? "#" + it.resource_id : ""}` : "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: "#888" }}>
                      {it.ip_address || "—"}
                    </TableCell>
                    <TableCell>
                      {it.details && (
                        <Tooltip title="JSON detayı">
                          <IconButton size="small" onClick={() => setDetail(it)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" p={2}>
            <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} color="primary" />
          </Box>
        )}
      </Card>

      {/* Detay modal: details JSON + user_agent */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Audit #{detail?.id} — {detail?.action}
        </DialogTitle>
        <DialogContent dividers>
          {detail && (
            <Box>
              <Typography variant="caption" color="text.secondary">Zaman</Typography>
              <Typography variant="body2" mb={1.5}>{fmtDateTime(detail.created_at)}</Typography>

              <Typography variant="caption" color="text.secondary">Yapan</Typography>
              <Typography variant="body2" mb={1.5}>
                {detail.actor_name || detail.actor_email || `#${detail.actor_id}`}
                {detail.ip_address ? ` · ${detail.ip_address}` : ""}
              </Typography>

              <Typography variant="caption" color="text.secondary">User-Agent</Typography>
              <Typography variant="body2" mb={1.5} sx={{ fontFamily: "monospace", fontSize: 11, wordBreak: "break-all" }}>
                {detail.user_agent || "—"}
              </Typography>

              {detail.resource_type && (
                <>
                  <Typography variant="caption" color="text.secondary">Kaynak</Typography>
                  <Typography variant="body2" mb={1.5}>
                    {detail.resource_type}{detail.resource_id ? " #" + detail.resource_id : ""}
                  </Typography>
                </>
              )}

              <Typography variant="caption" color="text.secondary">Detaylar (JSON)</Typography>
              <Box
                component="pre"
                sx={{
                  bgcolor: "#0f172a", color: "#e2e8f0", p: 2, borderRadius: 1.5,
                  fontSize: 12, maxHeight: 400, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word"
                }}
              >
                {(() => {
                  try {
                    return JSON.stringify(JSON.parse(detail.details || "null"), null, 2);
                  } catch {
                    return detail.details || "(boş)";
                  }
                })()}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Kapat</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
